import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useTranslation from '~/hooks/useTranslation';
import { useCustomerTravelplans } from '~/hooks/myEnvironment/useCustomerTravelplans';
import myEnvironmentStyles from '~/styles/myEnvironment/my-environment.module.css';
import reservationsStyles from '~/styles/myEnvironment/reservations.module.css';
import utilStyles from '~/styles/utils.module.css';
import Spinner from '~/components/General/Spinner';
import Pagination from '~/components/General/Pagination';
import Alert from '~/components/General/Alert';
import ReservationsList from '~/components/MyEnvironment/ReservationsList';
import GetInTouch from '~/components/MyEnvironment/GetInTouch';
import { useOptions } from '~/hooks/useOptions';
import { InferredMyEnvironmentOptions } from '~/types/options';
import cnh from '~/helpers/cnh';
import PlainTextField from '~/components/Form/PlainTextField';
import MyEnvironmentLayout from './Layout';
import IconBriefcase from '~icons/tabler/briefcase';
import IconSearch from '~icons/tabler/search';

export default function Reservations() {
  const translate = useTranslation();
  const client = useQueryClient();
  const options = useOptions() as InferredMyEnvironmentOptions;

  const [filters, setFilters] = useState({
    page: 1,
    per_page: 12,
    search: '',
  });

  const onSearch = useCallback(() => {
    setFilters({ ...filters, page: 1 });
    client.invalidateQueries({ queryKey: ['customerTravelplans'] });
  }, [client, filters]);

  const onSearchKeyup = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter') {
        onSearch();
      }
    },
    [onSearch],
  );

  const { data, isFetching, error } = useCustomerTravelplans({
    locale: options.locale,
    office_id: options.officeId,
    department_id: options.departmentId,
    ...filters,
  });

  const hasReservations = data?.data && data?.data.length > 0;

  const reservations = useMemo(() => {
    const upcomingReservations = data?.data.filter((reservation) => {
      return reservation.start_date && new Date(reservation.start_date) > new Date();
    });

    const pastReservations = data?.data.filter((reservation) => {
      return reservation.start_date && new Date(reservation.start_date) < new Date();
    });

    return (
      <>
        {upcomingReservations && upcomingReservations.length > 0 && (
          <>
            <h3 className={myEnvironmentStyles.subTitle}>
              {translate('myEnvironment.reservations.upcoming_title')}
            </h3>

            <ReservationsList reservations={upcomingReservations} />
          </>
        )}

        {pastReservations && pastReservations.length > 0 && (
          <>
            <h3 className={myEnvironmentStyles.subTitle}>
              {translate('myEnvironment.reservations.past_title')}
            </h3>

            <ReservationsList reservations={pastReservations} />
          </>
        )}

        {data && data.total > data.per_page && (
          <Pagination
            currentPage={data.current_page}
            total={data.total}
            perPage={data.per_page}
            onPageChange={(pageNumber) => setFilters({ ...filters, page: pageNumber })}
          />
        )}
      </>
    );
  }, [data, translate, filters]);

  return (
    <MyEnvironmentLayout>
      <div className={myEnvironmentStyles.titleContainer}>
        <h2 className={myEnvironmentStyles.title}>
          {translate('myEnvironment.reservations.title')}
          {data && data.total > 0 && (
            <span className={myEnvironmentStyles.titleCount}>
              {`${data?.total}`}
            </span>
          )}
        </h2>

        {data?.total && data?.total > 10 && (
          <div className={reservationsStyles.search}>
            <PlainTextField
              label={translate('myEnvironment.reservations.search')}
              value={filters.search}
              placeholder={translate('myEnvironment.reservations.search_placeholder')}
              visuallyHiddenLabel
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
              onKeyUp={onSearchKeyup}
            />

            <button
              type="button"
              className={reservationsStyles.searchButton}
              onClick={onSearch}
              disabled={isFetching}
            >
              <IconSearch />
              <span className={utilStyles.srOnly}>
                {translate('myEnvironment.reservations.search')}
              </span>
            </button>
          </div>
        )}
      </div>

      <Alert
        type="danger"
        error={error}
      />

      {isFetching && (
        <Spinner />
      )}

      {hasReservations && reservations}

      {!hasReservations && !isFetching && (
        <div className={reservationsStyles.empty}>
          <IconBriefcase />
          {translate('myEnvironment.reservations.empty')}
        </div>
      )}

      {options.contactEmail && (
        <GetInTouch
          className={cnh(myEnvironmentStyles.getInTouch, utilStyles.mtMedium)}
          email={options.contactEmail}
        />
      )}
    </MyEnvironmentLayout>
  );
}
